'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { VoiceInfoEditDialog } from '@/app/components/shared/VoiceInfoEditDialog';
import { toast } from '@/app/components/ui/use-toast';
import type { SourceAudio } from '@/app/types';
import { isVoiceNameExistsError } from '@/app/utils/client';
import { updateVoice } from '../apis';
import { MAX_VOICE_DESCRIPTION_LENGTH, MAX_VOICE_NAME_LENGTH } from '../constants';
import { AudioPlayer } from './AudioPlayer';

interface VoiceInfoEditorProps {
  voiceId: string;
  audio: SourceAudio;
  voiceName: string;
  voiceDescription: string;
  onVoiceNameChange: (value: string) => void;
  onVoiceDescriptionChange: (value: string) => void;
}

export function VoiceInfoEditor({
  voiceId,
  audio,
  voiceName,
  voiceDescription,
  onVoiceNameChange,
  onVoiceDescriptionChange,
}: VoiceInfoEditorProps) {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const [draftName, setDraftName] = useState(voiceName);
  const [draftDescription, setDraftDescription] = useState(voiceDescription);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDraftName(voiceName);
      setDraftDescription(voiceDescription);
    }
  }, [open, voiceName, voiceDescription]);

  const handleConfirm = async () => {
    if (draftName === voiceName && draftDescription === voiceDescription) {
      setOpen(false);
      return;
    }

    setIsSaving(true);
    try {
      const result = await updateVoice({
        path: {
          'voice.voice_id': voiceId,
        },
        body: {
          mask: 'name,description',
          voice: {
            voice_id: voiceId,
            name: draftName,
            description: draftDescription,
          },
        },
      });
      if (result.error) {
        throw result.error;
      }
    } catch (error: unknown) {
      toast({
        type: 'error',
        message: isVoiceNameExistsError(error)
          ? t('tools.voiceClone.validation.nameDuplicate')
          : t('agentProject.operationSelect.error'),
      });
      setIsSaving(false);
      return;
    }

    onVoiceNameChange(draftName);
    onVoiceDescriptionChange(draftDescription);
    setOpen(false);
    setIsSaving(false);
  };

  return (
    <>
      <AudioPlayer audio={audio} onEdit={() => setOpen(true)} />
      <VoiceInfoEditDialog
        open={open}
        onOpenChange={setOpen}
        name={draftName}
        description={draftDescription}
        onNameChange={setDraftName}
        onDescriptionChange={setDraftDescription}
        onCancel={() => setOpen(false)}
        onConfirm={handleConfirm}
        isSaving={isSaving}
        nameMaxLength={MAX_VOICE_NAME_LENGTH}
        descriptionMaxLength={MAX_VOICE_DESCRIPTION_LENGTH}
      />
    </>
  );
}
